import React, { useState, useEffect } from 'react';
import { RankingSnapshot, InvoiceAverages } from '../services/api';
import apiService from '../services/api';
import { getActiveHoursLabel } from '../config/activeHours';

interface SalesDataTabProps {
  ranking: RankingSnapshot;
  invoiceAverages?: InvoiceAverages | null;
  loadingInvoiceData?: boolean;
}

interface DailySalesRow {
  date: string;
  quantity: number;
  revenue: number;
  sales_count: number;
  avg_price: number;
}

interface SalesSnapshotData {
  snapshot_date: string;
  period_start: string;
  period_end: string;
  daily_sales: DailySalesRow[];
  dpd_calculations: {
    dpd_0: {
      date: string;
      sales: number;
      label: string;
    };
    dpd_1: {
      date: string;
      sales: number;
      label: string;
    };
    dpd_3: {
      dates: string[];
      sales_detail: number[];
      sales_avg: number;
      label: string;
    };
  };
  total_period: {
    total_quantity: number;
    total_revenue: number;
    total_sales: number;
    days_with_sales: number;
  };
}

const SalesDataTab: React.FC<SalesDataTabProps> = ({ ranking, invoiceAverages, loadingInvoiceData }) => {
  const [salesData, setSalesData] = useState<SalesSnapshotData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Debug log
  console.log('SalesDataTab - Invoice Averages:', invoiceAverages);
  console.log('SalesDataTab - Loading Invoice Data:', loadingInvoiceData);

  useEffect(() => {
    if (!ranking.calculation_timestamp || !ranking.sku) return;

    const fetchSalesData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // calculation_timestamp is guaranteed to be non-null here due to the check above
        const response = await apiService.getSalesForSnapshot(
          ranking.sku, 
          ranking.calculation_timestamp as string,
          7
        );
        setSalesData(response);
      } catch (err) {
        console.error('Error fetching sales data:', err);
        setError('Erro ao carregar dados de vendas');
      } finally {
        setLoading(false);
      }
    };

    fetchSalesData();
  }, [ranking.calculation_timestamp, ranking.sku]);

  const formatCurrency = (value: number): string => {
    return `€${value.toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatNumber = (value: number | null, decimals = 0): string => {
    if (value === null || value === undefined) return '-';
    return value.toLocaleString('pt-PT', { 
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals 
    });
  };

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit' });
  };

  const formatDateTime = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleString('pt-PT', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDayLabel = (date: string): string => {
    if (!salesData) return '';
    
    const snapshotDate = new Date(salesData.snapshot_date).toISOString().split('T')[0];
    const targetDate = new Date(date).toISOString().split('T')[0];
    
    if (targetDate === snapshotDate) return 'Hoje';
    
    const daysBefore = Math.floor(
      (new Date(snapshotDate).getTime() - new Date(targetDate).getTime()) / (1000 * 60 * 60 * 24)
    );
    
    if (daysBefore === 1) return 'D-1';
    if (daysBefore > 0) return `D-${daysBefore}`;
    return '';
  };

  const calculateDPDDifference = (rankingValue: number | null, realValue: number): {
    diff: number;
    percentage: number;
  } => {
    if (!rankingValue && !realValue) return { diff: 0, percentage: 0 };
    if (!rankingValue) return { diff: realValue, percentage: 100 };
    
    const diff = realValue - rankingValue;
    const percentage = rankingValue > 0 ? (Math.abs(diff) / rankingValue * 100) : 100;
    
    return { diff, percentage };
  };

  if (loading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Carregando...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  if (!salesData) {
    return (
      <div className="alert alert-info" role="alert">
        Sem dados de vendas disponíveis
      </div>
    );
  }

  return (
    <div className="sales-data-tab">
      {/* Header com informações do período */}
      <div className="card mb-3">
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <h6 className="text-muted">⏰ Snapshot</h6>
              <p className="mb-0">{formatDateTime(salesData.snapshot_date)}</p>
            </div>
            <div className="col-md-6">
              <h6 className="text-muted">📅 Período Analisado</h6>
              <p className="mb-0">
                {formatDate(salesData.period_start)} a {formatDate(salesData.period_end)} 
                <span className="text-muted ms-2">(7 dias)</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Card com Médias de Faturas */}
      <div className="card mb-3">
        <div className="card-header bg-light">
          <h6 className="mb-0">📊 Médias de Faturas por Dia</h6>
        </div>
        <div className="card-body">
          {loadingInvoiceData ? (
            <div className="text-center">
              <div className="spinner-border spinner-border-sm text-primary" role="status">
                <span className="visually-hidden">Carregando...</span>
              </div>
            </div>
          ) : invoiceAverages ? (
              <div className="row g-3">
                <div className="col-md-3">
                  <div className="text-center">
                    <div className="small text-muted">Média 30 dias</div>
                    <div className="h5 mb-0">{formatNumber(invoiceAverages.media_30d, 1)}</div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="text-center">
                    <div className="small text-muted">Média 90 dias</div>
                    <div className="h5 mb-0">{formatNumber(invoiceAverages.media_90d, 1)}</div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="text-center">
                    <div className="small text-muted">Média 365 dias</div>
                    <div className="h5 mb-0">{formatNumber(invoiceAverages.media_365d, 1)}</div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="text-center">
                    <div className="small text-muted">Média Global</div>
                    <div className="h5 mb-0">{formatNumber(invoiceAverages.media_global, 1)}</div>
                  </div>
                </div>
                <div className="col-12 mt-2">
                  <div className="text-center">
                    <small className="text-muted">
                      Total de faturas analisadas: {invoiceAverages.total_faturas}
                    </small>
                  </div>
                </div>
              </div>
          ) : (
            <div className="text-center text-muted">
              <p className="mb-0">Sem dados de faturas disponíveis para este produto</p>
            </div>
          )}
        </div>
      </div>

      {/* Tabela de Vendas Diárias */}
      <div className="card mb-3">
        <div className="card-header">
          <h6 className="mb-0">📊 Vendas Diárias</h6>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-sm table-hover mb-0">
              <thead>
                <tr>
                  <th>Data</th>
                  <th className="text-end" title="Quantidade total de unidades vendidas">Unidades</th>
                  <th className="text-end" title="Receita total do dia">Receita</th>
                  <th className="text-end" title="Número de faturas/documentos de venda">Faturas</th>
                  <th className="text-end" title="Preço médio por unidade">Preço Médio</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {salesData.daily_sales.map((day, index) => {
                  const dayLabel = getDayLabel(day.date);
                  const isSnapshotDay = dayLabel === 'Hoje';
                  
                  return (
                    <tr key={day.date} className={isSnapshotDay ? 'table-warning' : ''}>
                      <td>
                        {formatDate(day.date)}
                        {isSnapshotDay && <span className="badge bg-warning ms-2">⭐</span>}
                      </td>
                      <td className="text-end">{day.quantity}</td>
                      <td className="text-end">{formatCurrency(day.revenue)}</td>
                      <td className="text-end">{day.sales_count}</td>
                      <td className="text-end">{formatCurrency(day.avg_price)}</td>
                      <td>
                        <span className={`badge ${isSnapshotDay ? 'bg-warning' : 'bg-secondary'}`}>
                          {dayLabel}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="table-light">
                  <td><strong>Total</strong></td>
                  <td className="text-end"><strong>{salesData.total_period.total_quantity}</strong></td>
                  <td className="text-end"><strong>{formatCurrency(salesData.total_period.total_revenue)}</strong></td>
                  <td className="text-end"><strong>{salesData.total_period.total_sales}</strong></td>
                  <td className="text-end">
                    <strong>
                      {salesData.total_period.total_quantity > 0 
                        ? formatCurrency(salesData.total_period.total_revenue / salesData.total_period.total_quantity)
                        : '-'}
                    </strong>
                  </td>
                  <td>
                    <span className="badge bg-info">
                      {salesData.total_period.days_with_sales} dias
                    </span>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      {/* Comparação DPD */}
      <div className="card">
        <div className="card-header">
          <h6 className="mb-0">📈 Comparação DPD (Ranking vs Real)</h6>
        </div>
        <div className="card-body">
          {/* DPD 0 */}
          <div className="mb-3 p-3 border rounded">
            <div className="row align-items-center">
              <div className="col-md-3">
                <strong>DPD 0</strong>
                <div className="small text-muted">{salesData.dpd_calculations.dpd_0.label}</div>
                <div className="small text-muted">{formatDate(salesData.dpd_calculations.dpd_0.date)}</div>
              </div>
              <div className="col-md-3">
                <div className="text-muted small">Ranking (DPD)</div>
                <div className="h5 mb-0">{ranking.dpd_0?.toFixed(1) || '-'}</div>
              </div>
              <div className="col-md-3">
                <div className="text-muted small">Real (unidades)</div>
                <div className="h5 mb-0">{salesData.dpd_calculations.dpd_0.sales}</div>
              </div>
              <div className="col-md-3 text-end">
                {(() => {
                  const { diff, percentage } = calculateDPDDifference(ranking.dpd_0, salesData.dpd_calculations.dpd_0.sales);
                  return (
                    <div>
                      <div className="text-muted small">Diferença</div>
                      <div className="h6 mb-0">
                        {diff > 0 ? '+' : ''}{diff.toFixed(1)} unidades
                      </div>
                      <div className="small text-muted">({percentage.toFixed(1)}%)</div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* DPD 1 */}
          <div className="mb-3 p-3 border rounded">
            <div className="row align-items-center">
              <div className="col-md-3">
                <strong>DPD 1</strong>
                <div className="small text-muted">{salesData.dpd_calculations.dpd_1.label}</div>
                <div className="small text-muted">{formatDate(salesData.dpd_calculations.dpd_1.date)}</div>
              </div>
              <div className="col-md-3">
                <div className="text-muted small">Ranking (DPD)</div>
                <div className="h5 mb-0">{ranking.dpd_1?.toFixed(1) || '-'}</div>
              </div>
              <div className="col-md-3">
                <div className="text-muted small">Real (unidades)</div>
                <div className="h5 mb-0">{salesData.dpd_calculations.dpd_1.sales}</div>
              </div>
              <div className="col-md-3 text-end">
                {(() => {
                  const { diff, percentage } = calculateDPDDifference(ranking.dpd_1, salesData.dpd_calculations.dpd_1.sales);
                  return (
                    <div>
                      <div className="text-muted small">Diferença</div>
                      <div className="h6 mb-0">
                        {diff > 0 ? '+' : ''}{diff.toFixed(1)} unidades
                      </div>
                      <div className="small text-muted">({percentage.toFixed(1)}%)</div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* DPD 3 */}
          <div className="mb-3 p-3 border rounded">
            <div className="row align-items-center">
              <div className="col-md-3">
                <strong>DPD 3</strong>
                <div className="small text-muted">{salesData.dpd_calculations.dpd_3.label}</div>
                <div className="small text-muted">
                  {salesData.dpd_calculations.dpd_3.dates.map(d => formatDate(d)).join(', ')}
                </div>
              </div>
              <div className="col-md-3">
                <div className="text-muted small">Ranking (DPD)</div>
                <div className="h5 mb-0">{ranking.dpd_3?.toFixed(1) || '-'}</div>
              </div>
              <div className="col-md-3">
                <div className="text-muted small">Real (média unidades)</div>
                <div className="h5 mb-0">{salesData.dpd_calculations.dpd_3.sales_avg.toFixed(1)}</div>
                <div className="small text-muted">
                  ({salesData.dpd_calculations.dpd_3.sales_detail.join(' + ')}) / 3
                </div>
              </div>
              <div className="col-md-3 text-end">
                {(() => {
                  const { diff, percentage } = calculateDPDDifference(ranking.dpd_3, salesData.dpd_calculations.dpd_3.sales_avg);
                  return (
                    <div>
                      <div className="text-muted small">Diferença</div>
                      <div className="h6 mb-0">
                        {diff > 0 ? '+' : ''}{diff.toFixed(1)} unidades
                      </div>
                      <div className="small text-muted">({percentage.toFixed(1)}%)</div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* DPD 7 - V2.0 */}
          {ranking.dpd_7 !== null && ranking.dpd_7 !== undefined && (
            <div className="p-3 border rounded bg-light">
              <div className="row align-items-center">
                <div className="col-md-3">
                  <strong>
                    <i className="bi bi-clock-history text-primary me-2"></i>
                    DPD 7
                  </strong>
                  <div className="small text-muted">Média últimos 7 dias</div>
                  <div className="small">
                    <span className={`badge bg-${
                      ranking.penalty_v2_category === 'ultra-fast' ? 'danger' :
                      ranking.penalty_v2_category === 'fast' ? 'warning' :
                      ranking.penalty_v2_category === 'medium' ? 'info' :
                      ranking.penalty_v2_category === 'slow' ? 'success' :
                      'secondary'
                    }`}>
                      {ranking.penalty_v2_category?.toUpperCase() || 'N/A'}
                    </span>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="text-muted small">Valor (Ranking)</div>
                  <div className="h5 mb-0 text-primary">{ranking.dpd_7.toFixed(1)}</div>
                </div>
                <div className="col-md-6">
                  <div className="alert alert-info p-2 mb-0">
                    <i className="bi bi-info-circle me-2"></i>
                    <small>
                      <strong>Base para Penalização V2.0:</strong> Este valor classifica o produto por velocidade
                      e determina a agressividade da penalização temporal ({getActiveHoursLabel()} apenas).
                      {ranking.penalty_v2_category === 'ultra-fast' && ' Produtos ultra-rápidos são penalizados mais agressivamente.'}
                      {ranking.penalty_v2_category === 'fast' && ' Produtos rápidos têm penalização média-alta.'}
                      {ranking.penalty_v2_category === 'medium' && ' Produtos médios têm penalização balanceada.'}
                      {ranking.penalty_v2_category === 'slow' && ' Produtos lentos têm penalização suave.'}
                    </small>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SalesDataTab;